import {
  useDeleteMemberTraitMutation,
  useGetMemberTraitsQuery,
} from "@apis/traits/useTraits"
import { DataTable } from "@components/DataTable/DataTable"
import type { ITrait } from "@apis/traits/types"
import type { ColumnDef } from "@tanstack/react-table"
import { AddMemberTraitsForm } from "./AddMemberTraitsForm"
import { ButtonCell } from "@utils/table/columnFactories"
import { Loader2 } from "lucide-react"
import { toast } from "sonner"
import type { AxiosError } from "axios"

const EditMemberTraits = ({
  prospect_guid,
}: {
  prospect_guid: string | undefined
}) => {
  const getMemberTraits = useGetMemberTraitsQuery(prospect_guid)

  const { mutate } = useDeleteMemberTraitMutation()

  const tableColumnsMemberTraits: ColumnDef<ITrait>[] = [
    {
      accessorKey: "trait_name",
      header: "Trait",
    },
    {
      accessorKey: "reason",
      header: "Reason",
    },
    {
      id: "delete",
      header: "",
      cell: (props) => (
        <ButtonCell
          {...props}
          variant="destructive"
          label="Delete"
          onClick={(rowData) => {
            mutate(
              {
                trait_code: rowData.trait_code,
                prospect_guid: rowData.prospect_guid,
              },
              {
                onSuccess: () => {
                  toast.success("Trait deleted!")
                },
                onError: (error: string | AxiosError) =>
                  toast.error(`Error deleting trait! ${error}`),
              },
            )
          }}
        />
      ),
    },
  ]

  if (getMemberTraits.isPending) {
    return (
      <Loader2 className="tw:h-6 tw:w-6 tw:animate-spin tw:text-gray-500" />
    )
  }

  if (getMemberTraits.isError) {
    return (
      <div className="tw:text-red-500">
        There was an error loading member traits. Error:{" "}
        {getMemberTraits.error.message}
      </div>
    )
  }

  return (
    <div className="tw:flex tw:flex-col tw:gap-10">
      <DataTable
        columns={tableColumnsMemberTraits}
        data={getMemberTraits.data ?? []}
        pending={getMemberTraits.isPending}
      />
      <AddMemberTraitsForm prospect_guid={prospect_guid as string} />
    </div>
  )
}

export default EditMemberTraits
